// @ts-nocheck
/**
 * Single source of truth for "saved exam" display values (cohort vs preferences).
 * Use getSavedExamForDisplay() everywhere to avoid duplicating logic.
 * Returns { savedName, savedIcon, savedRedirectUrl }.
 *
 * getHomeRedirectUrl(rootUrl, eventDetail) – single source for home/logo redirect URL.
 * applyHomeRedirect(ev) – updates all [data-logo-redirect] and [data-home-redirect] links.
 */
(function (global) {
  "use strict";

  function isCustomerLoggedIn() {
    return typeof global.hasCustomer !== "undefined" && global.hasCustomer === true;
  }

  function fromPreferences(prefs) {
    if (!prefs || !prefs.exams || !prefs.exams.length) return null;
    const lastExam = prefs.exams[prefs.exams.length - 1];
    return {
      name: typeof lastExam === "string" ? lastExam.trim().replace(/-/g, " ") : "",
      icon: (prefs.examIcon && String(prefs.examIcon)) || "",
      url: (prefs.redirection_url && String(prefs.redirection_url).trim())
        || (prefs.redirectionUrl && String(prefs.redirectionUrl).trim())
        || null
    };
  }

  /**
   * The cohort the student picked for their account (customer.metafields.custom.preferences /
   * cohort_name, set server-side in gtm-global-variables.liquid — same data the logo's SSR href
   * is computed from in blocks/_header-logo.liquid). This is the source of truth for "home"
   * contexts (logo click, homepage), regardless of cohorts explored mid-session.
   */
  function getAccountCohort() {
    if (!isCustomerLoggedIn()) return null;
    try {
      const prefsRaw = global.localStorage.getItem("preferences");
      const prefsData = prefsRaw ? (function () { try { return JSON.parse(prefsRaw); } catch (e) { return null; } })() : null;
      const fromLocalPrefs = fromPreferences(prefsData);
      if (fromLocalPrefs && fromLocalPrefs.name) return fromLocalPrefs;

      const customerData = global.__CUSTOMER_DATA__ || {};
      const fromServerPrefs = fromPreferences(customerData.preferences);
      const name = (fromServerPrefs && fromServerPrefs.name) || (customerData.cohort_name && String(customerData.cohort_name).trim()) || "";
      if (!name) return null;
      return {
        name: name,
        icon: (fromServerPrefs && fromServerPrefs.icon) || "",
        url: (fromServerPrefs && fromServerPrefs.url) || null
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Drops the "explored cohort" session override and restores the student's account cohort.
   * Exploring other cohorts (exam-selector / exam-preparation) sets selectedExamName +
   * cohortChangedInSession so the explored cohort sticks while browsing it — but it must not
   * stick once the student goes "home" (logo click / homepage), where they expect to see their
   * own cohort again.
   */
  function restoreAccountCohort() {
    try {
      const account = getAccountCohort();
      if (!account || !account.name) return false;
      global.sessionStorage.removeItem("cohortChangedInSession");
      global.localStorage.setItem("selectedExamName", account.name);
      if (account.icon) global.localStorage.setItem("selectedExamIcon", account.icon);
      if (account.url) global.localStorage.setItem("selectedExamRedirectUrl", account.url);
      return true;
    } catch (e) {
      return false;
    }
  }

  // If a cohort explored mid-session is still flagged when the student lands on the homepage,
  // drop the override before anything reads "saved exam" so the homepage shows their own cohort.
  function resetSessionOverrideOnHome() {
    try {
      const pathname = (global.location && global.location.pathname) || "";
      const sessionCohortChanged = global.sessionStorage.getItem("cohortChangedInSession") === "true";
      if (pathname === "/" && isCustomerLoggedIn() && sessionCohortChanged) {
        restoreAccountCohort();
      }
    } catch (e) {}
  }

  function getSavedExamForDisplay() {
    let savedName = null;
    let savedIcon = null;
    let savedRedirectUrl = null;
    try {
      const isLoggedIn = isCustomerLoggedIn();
      const sessionCohortChanged = global.sessionStorage.getItem("cohortChangedInSession") === "true";

      const prefsRaw = global.localStorage.getItem("preferences");
      const prefsData = prefsRaw ? (function () { try { return JSON.parse(prefsRaw); } catch (e) { return null; } })() : null;
      const fromPrefs = fromPreferences(prefsData);

      const selectedName = global.localStorage.getItem("selectedExamName");
      const selectedIcon = global.localStorage.getItem("selectedExamIcon");
      const selectedUrl = global.localStorage.getItem("selectedExamRedirectUrl");
      const hasSelectedInSession = (selectedName && selectedName.trim()) || (selectedIcon && selectedIcon.trim());

      if (isLoggedIn) {
        const account = getAccountCohort();
        // After login, preferences (from API) is source of truth unless user changed cohort in this session and we have their selection
        if (sessionCohortChanged && hasSelectedInSession) {
          savedName = selectedName || (fromPrefs && fromPrefs.name) || (account && account.name) || "";
          savedIcon = selectedIcon || (fromPrefs && fromPrefs.icon) || (account && account.icon) || "";
          savedRedirectUrl = selectedUrl || (fromPrefs && fromPrefs.url) || (account && account.url) || null;
        } else {
          savedName = (fromPrefs && fromPrefs.name) || selectedName || (account && account.name) || "";
          savedIcon = (fromPrefs && fromPrefs.icon) || selectedIcon || (account && account.icon) || "";
          savedRedirectUrl = (fromPrefs && fromPrefs.url) || selectedUrl || (account && account.url) || null;
        }
      } else {
        savedName = (fromPrefs && fromPrefs.name) || selectedName || "";
        savedIcon = (fromPrefs && fromPrefs.icon) || selectedIcon || "";
        savedRedirectUrl = (fromPrefs && fromPrefs.url) || selectedUrl || null;
      }
    } catch (e) {}
    return { savedName, savedIcon, savedRedirectUrl };
  }

  /**
   * Returns the URL to use for "home" / logo links (root vs cohort redirect).
   * @param {string} rootUrl - e.g. routes.root_url
   * @param {{ redirection_url?: string, examName?: string }|null} [eventDetail] - from examSelected event
   */
  function getHomeRedirectUrl(rootUrl, eventDetail) {
    let pathname = (global.location && global.location.pathname) || "";
    let cohortRedirectUrl = "";
    let name = "";
    let saved = {};

    if (eventDetail && eventDetail.redirection_url) {
      cohortRedirectUrl = (eventDetail.redirection_url || "").trim();
      name = (eventDetail.examName || "").trim();
    }

    if (!cohortRedirectUrl && !name) {
      saved = getSavedExamForDisplay();
      name = saved.savedName || "";
      cohortRedirectUrl = (saved.savedRedirectUrl || "").trim() || "";
    }

    let hasCohort = (name && name !== "Select Exam" && name !== "Select Exam/Class") || !!cohortRedirectUrl;
    if (!hasCohort) {
      return rootUrl
    };

    // Only when current page is a /pages/ URL: redirect to cohort page; all other pages → homepage
    let pathToCheck = cohortRedirectUrl || pathname;
    if (pathToCheck.indexOf("/pages/") !== -1) {
      return cohortRedirectUrl;
    }

    return rootUrl;
  }

  /**
   * Updates href on all elements with data-logo-redirect or data-home-redirect (they must have data-root-url).
   * Call with no arg on load; pass event on examSelected.
   */
  function applyHomeRedirect(ev) {
    let detail = (ev && ev.detail) || null;
    let links = global.document && global.document.querySelectorAll
      ? global.document.querySelectorAll("[data-logo-redirect], [data-home-redirect]")
      : [];
    for (let i = 0; i < links.length; i++) {
      let link = links[i];
      if (!link) continue;
      let base = (link.getAttribute("data-root-url") || "").trim() || "/";
      link.setAttribute("href", getHomeRedirectUrl(base, detail));
    }
  }

  function runApplyWhenReady() {
    function run() {
      applyHomeRedirect(null);
    }

    if (global.document.readyState === "loading") {
      global.document.addEventListener("DOMContentLoaded", () => {
        run();
      });
    } else {
      run();
    }

    global.addEventListener("examSelected", () => {
      run();
    });
  }

  // Going "home" (logo or footer Home nav) is the explicit signal that the student wants their
  // own cohort back, not whatever they were exploring. Restore + refresh hrefs before the
  // browser's default navigation runs (synchronous, so no preventDefault needed).
  function bindHomeLinkRestore() {
    global.document.addEventListener("click", function (e) {
      const link = e.target.closest("[data-logo-redirect], [data-home-redirect]");
      if (!link) return;
      if (!isCustomerLoggedIn()) return;
      if (global.sessionStorage.getItem("cohortChangedInSession") !== "true") return;
      if (restoreAccountCohort()) {
        applyHomeRedirect(null);
      }
    });
  }

  // FIX: Setting globals on window synchronously during defer execution
  // triggers a renderer freeze in certain Shopify contexts (e.g. post-multipass
  // redirect in incognito). Dispatch a custom event instead so inline section
  // scripts (parsed before this defer script) can react immediately, and delay
  // the global assignments to after the current task completes.
  if (typeof global.document !== "undefined") {
    resetSessionOverrideOnHome();

    global.dispatchEvent(new CustomEvent('examDisplayReady', {
      detail: {
        getSavedExamForDisplay: getSavedExamForDisplay,
        getHomeRedirectUrl: getHomeRedirectUrl,
        applyHomeRedirect: applyHomeRedirect
      }
    }));

    runApplyWhenReady();
    bindHomeLinkRestore();
  }
  console.log("exam-display-utils loaded");
})(typeof window !== "undefined" ? window : this);